import type { Dispatch } from 'redux';
import { Music24LimitPayload, music24LimitMessage, readMusic24Limit } from 'fym-common/src/errors/music24Limits';
import { Music24QuotaKind } from 'fym-common/src/license/music24Plans';
import { SMLUpgradeFeature } from '../config/music24PlanCapabilities';
import { openSMLUpgradePrompt } from '../../../redux/slices/appInfo/slice';
import { showToast } from '../../../redux/slices/toasts/slice';
import { ToastType } from '../../../redux/slices/toasts/state';

export interface M24LimitError {
  code: string;
  feature: SMLUpgradeFeature;
  limit: number | null;
  used: number | null;
  kind: Music24QuotaKind | null;
  resetsAt: string | null;
}

const FEATURE_BY_CODE: Record<string, SMLUpgradeFeature> = {
  M24_LIMIT_PAGES: SMLUpgradeFeature.pages,
  M24_LIMIT_ARTISTS: SMLUpgradeFeature.artists,
  M24_LIMIT_SEATS: SMLUpgradeFeature.seats,
  M24_LIMIT_SOCIAL: SMLUpgradeFeature.socialAccounts,
  M24_LIMIT_RELEASES: SMLUpgradeFeature.releases,
  // 403: the plan has fewer artists than the workspace, so this artist is read-only. More artists
  // on the plan make it editable again.
  M24_LIMIT_ARTIST_READ_ONLY: SMLUpgradeFeature.artists,
};

const FEATURE_BY_GATE: Record<string, SMLUpgradeFeature> = {
  canExportFans: SMLUpgradeFeature.fanExport,
  canUseCustomDomain: SMLUpgradeFeature.customDomain,
  canExport: SMLUpgradeFeature.csvExport,
  canFilterGeographically: SMLUpgradeFeature.geography,
  canFilterByMusicService: SMLUpgradeFeature.musicServiceFilter,
  canSeeEarnings: SMLUpgradeFeature.earnings,
  canSplitStreaming: SMLUpgradeFeature.streamingSplit,
  canPushOldSongs: SMLUpgradeFeature.oldSongs,
  // X (spec 2026-10-22): connect on a plan without X opens the X prompt (Pro and up).
  canUseX: SMLUpgradeFeature.x,
  // A new connect through the posting partner (Zernio) on Free or the trial: the generic plan offer.
  canAutoPostToMeta: SMLUpgradeFeature.generic,
  // More than 1 post a day in an Auto campaign (Free and the trial: 1): the generic plan offer.
  reelsPlanPostsPerDay: SMLUpgradeFeature.generic,
  canCustomizeKitLook: SMLUpgradeFeature.kitLook,
  canDownloadKitZip: SMLUpgradeFeature.kitZip,
  canRenderFullSongVideos: SMLUpgradeFeature.fullVideo,
  // AI clips and the directed video (Free and the trial make no AI footage).
  canMakeAiFootage: SMLUpgradeFeature.aiFootage,
  canUsePresave: SMLUpgradeFeature.presave,
  // Outreach (spec 2026-10-11 §10): pitching at all, autopilot and the contact export.
  outreach: SMLUpgradeFeature.outreach,
  outreachAutopilot: SMLUpgradeFeature.outreachAutopilot,
  outreachExport: SMLUpgradeFeature.outreachExport,
  outreachMailboxes: SMLUpgradeFeature.outreachMailboxes,
};

/**
 * Gates that every plan has on: an upgrade does not change them, so a refusal opens no upgrade
 * prompt. The screen shows its own copy (AI reels: design `music24-ai-reels` a6).
 */
const NON_PLAN_GATES: ReadonlyArray<string> = ['canUseAiReels'];

const FEATURE_BY_KIND: Record<Music24QuotaKind, SMLUpgradeFeature> = {
  scheduledPost: SMLUpgradeFeature.scheduledPosts,
  videoCredit: SMLUpgradeFeature.videoCredits,
  aiInsight: SMLUpgradeFeature.aiInsights,
  // The AI pictures of the cover editor: the general plan prompt (no feature of its own yet).
  coverAiPicture: SMLUpgradeFeature.generic,
  // Outreach v2 has no reveal: an old `outreachReveal` answer opens the Outreach plan offer.
  outreachReveal: SMLUpgradeFeature.outreach,
  outreachSend: SMLUpgradeFeature.outreachSends,
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

function isQuotaKind(value: unknown): value is Music24QuotaKind {
  return typeof value === 'string' && value in FEATURE_BY_KIND;
}

/** The plan 2 payload, from any place a client can hold it. `readMusic24Limit` checks the code. */
export function readM24Error(error: unknown): Music24LimitPayload | null {
  const root = asRecord(error);
  if (root === null) {
    return null;
  }
  const candidates: unknown[] = [
    asRecord(root.data), // tRPC client error: data.m24Limit
    asRecord(asRecord(root.shape)?.data), // tRPC error shape
    root, // FYMAPIError with m24Limit (customBaseQuery)
    { m24Limit: root.cause }, // raw REST 402 body: { cause }
  ];
  for (const candidate of candidates) {
    const payload = readMusic24Limit(candidate);
    if (payload !== null) {
      return payload;
    }
  }
  return null;
}

export function parseM24LimitError(error: unknown): M24LimitError | null {
  const payload = readM24Error(error);
  if (payload === null || !payload.code.startsWith('M24_LIMIT_')) {
    return null;
  }
  if (payload.code === 'M24_LIMIT_FEATURE' && NON_PLAN_GATES.includes(payload.feature ?? '')) {
    return null;
  }
  const kind = isQuotaKind(payload.kind) ? payload.kind : null;
  let feature = FEATURE_BY_CODE[payload.code] ?? SMLUpgradeFeature.generic;
  if (payload.code === 'M24_LIMIT_QUOTA' && kind !== null) {
    feature = FEATURE_BY_KIND[kind];
  }
  if (payload.code === 'M24_LIMIT_FEATURE') {
    feature = FEATURE_BY_GATE[payload.feature ?? ''] ?? SMLUpgradeFeature.generic;
  }
  const raw = asRecord(payload) ?? {};
  return {
    code: payload.code,
    feature,
    limit: numberOrNull(raw.limit),
    used: numberOrNull(raw.used),
    kind,
    resetsAt: typeof payload.resetsAt === 'string' ? payload.resetsAt : null,
  };
}

/** The 429 code of a spent AI budget of Free and trial workspaces (music24-api `budgetError.ts`). */
export const M24_AI_BUDGET_FREE_EXHAUSTED = 'M24_AI_BUDGET_FREE_EXHAUSTED';

/**
 * True for a 429 `M24_AI_BUDGET_FREE_EXHAUSTED`: the AI of Free and trial workspaces is busy this
 * month, and an upgrade gives priority access. It reads the `cause` code only, so an app build
 * that does not know the code shows its generic error and never crashes.
 */
export function isAiBudgetFreeExhausted(error: unknown): boolean {
  const root = asRecord(error);
  if (root === null) {
    return false;
  }
  const causes = [asRecord(root.m24Limit), asRecord(root.cause), asRecord(asRecord(root.data)?.m24Limit)];
  return causes.some((cause) => cause?.code === M24_AI_BUDGET_FREE_EXHAUSTED);
}

/** The code of a write to the read-only demo artist (LaziiBee). */
export const M24_DEMO_READ_ONLY = 'M24_DEMO_READ_ONLY';

/**
 * The text of a `M24_DEMO_READ_ONLY` refusal: the server message when the error carries one, else
 * the shared copy. `null` for any other error.
 */
export function demoReadOnlyMessage(error: unknown): string | null {
  const payload = readM24Error(error);
  if (payload === null || payload.code !== M24_DEMO_READ_ONLY) {
    return null;
  }
  const message = asRecord(error)?.message;
  return typeof message === 'string' && message.trim() !== '' ? message : music24LimitMessage(payload);
}

/**
 * The one place every client dispatches the upgrade prompt from. `TRPCLoader`'s QueryCache
 * `onError`, `dispatchM24LimitPromptForMutation` (below) and `customBaseQuery`'s `catch` call
 * this instead of repeating parse-then-dispatch. A `M24_DEMO_READ_ONLY` refusal shows a toast instead.
 * A no-op for any other error that isn't a plan limit.
 */
export function dispatchM24LimitPrompt(dispatch: Dispatch, error: unknown): void {
  // design: music24-demo-artist a4. A write to the demo artist is not a plan limit: a plain toast
  // with the server text, never the upgrade prompt.
  const demoMessage = demoReadOnlyMessage(error);
  if (demoMessage !== null) {
    dispatch(
      showToast({ type: ToastType.info, description: demoMessage, autoHide: true, uniqueId: M24_DEMO_READ_ONLY })
    );
    return;
  }
  // A spent Free AI budget is not a plan limit of the payload kind: it opens the "AI is busy this
  // month" prompt, which offers the cheapest paid plan.
  if (isAiBudgetFreeExhausted(error)) {
    dispatch(openSMLUpgradePrompt({ feature: SMLUpgradeFeature.aiPriority }));
    return;
  }
  const limit = parseM24LimitError(error);
  if (limit === null) {
    return;
  }
  dispatch(
    openSMLUpgradePrompt({
      feature: limit.feature,
      limit: limit.limit,
      used: limit.used,
      kind: limit.kind,
      resetsAt: limit.resetsAt,
    })
  );
}

/**
 * `TRPCLoader`'s `MutationCache` `onError`. Same as `dispatchM24LimitPrompt`, except a mutation
 * that already shows its own inline message for a plan-limit refusal can opt out with
 * `meta: { skipM24LimitPrompt: true }` on its `useMutation` options — the prompt then never
 * opens, instead of opening and being closed a moment later once the component's own error UI
 * reacts. `team.accept`'s `M24_LIMIT_SEATS` case is the first user: the invitee cannot fix a
 * full team by upgrading their own workspace, so the global prompt would offer the wrong
 * checkout.
 */
export function dispatchM24LimitPromptForMutation(
  dispatch: Dispatch,
  error: unknown,
  mutation: { meta?: Record<string, unknown> }
): void {
  if (mutation.meta?.skipM24LimitPrompt === true) {
    return;
  }
  dispatchM24LimitPrompt(dispatch, error);
}
